import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  submitQCObservation,
  getUsersByProject,
  getProjectsForCurrentUser,
} from "../../api/index";
import {
  fetchTowersByProject,
  getCategoryTreeByProject,
  getLevelsByTowerId,
} from "../../api/index";

import {
  ArrowLeft,
  Plus,
  ChevronDown,
  MapPin,
  Users,
  Trash2,
  ClipboardList,
  Camera,
  ImageOff,
  X,
  PenLine,
  UploadCloud,
  Search,
} from "lucide-react";
import SafetyImageAnnotationModal from "../Safety/Safety_Inspection/SafetyImageAnnotationModal";
import SafetyCameraCaptureModal from "../Safety/Safety_Inspection/SafetyCameraCaptureModal";
import GeoFenceGuard from "../geofence/GeoFenceGuard";

const SEVERITY = {
  Critical: { fg: "#C1443A", bg: "#F5DCD9" },
  Major: { fg: "#B4761F", bg: "#F6E6CE" },
  Minor: { fg: "#3E5C76", bg: "#DEE6EC" },
};

const inputCls =
  "w-full rounded-lg border px-3.5 py-2.5 text-[14px] bg-white outline-none transition-shadow focus:ring-2";

function FieldLabel({ children, required }) {
  return (
    <label
      className="block text-[13px] font-semibold mb-1.5"
      style={{ color: "#3A4552" }}
    >
      {children}
      {required && <span style={{ color: "#C1443A" }}> *</span>}
    </label>
  );
}

function Select({ value, onChange, options, placeholder, getLabel, getValue }) {
  return (
    <div className="relative">
      <select
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className={`${inputCls} appearance-none pr-9 cursor-pointer`}
        style={{
          borderColor: "#DEDAD1",
          color: value ? "#1B2430" : "#9A968C",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => {
          const optionValue = getValue ? getValue(option) : option;
          const optionLabel = getLabel ? getLabel(option) : option;

          return (
            <option key={String(optionValue)} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>

      <ChevronDown
        size={16}
        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: "#6B7480" }}
      />
    </div>
  );
}

function PhotoThumb({ file, size = 72 }) {
  const preview = file instanceof File ? URL.createObjectURL(file) : null;

  if (!preview) {
    return (
      <div
        className="flex items-center justify-center rounded-md shrink-0"
        style={{
          width: size,
          height: size,
          background: "#E7E5E0",
          color: "#9A968C",
        }}
      >
        <ImageOff size={18} />
      </div>
    );
  }

  return (
    <img
      src={preview}
      alt={file.name}
      className="rounded-md shrink-0 object-cover border"
      style={{
        width: size,
        height: size,
        borderColor: "#DEDAD1",
      }}
      onLoad={() => URL.revokeObjectURL(preview)}
    />
  );
}

function createEmptyItem() {
  return {
    key: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
    repetition: "",
    gap_type: "",
    description: "",
    resolution_advised: "",
    attachments: [],
  };
}

// --- NEW: in-page camera capture modal ---------------------------------
function CameraCaptureModal({ open, onClose, onCapture }) {
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;

    let active = true;

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch((err) => {
        console.error("Failed to access camera:", err);
        setError("Unable to access camera. Please check permissions.");
      });

    return () => {
      active = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setError(null);
    onClose();
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        onCapture(file);
        handleClose();
      },
      "image/jpeg",
      0.92,
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-white rounded-xl overflow-hidden max-w-[520px] w-full">
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: "#DEDAD1" }}
        >
          <div className="text-[15px] font-bold" style={{ color: "#1B2430" }}>
            Take Photo
          </div>
          <button type="button" onClick={handleClose}>
            <X size={18} color="#6B7480" />
          </button>
        </div>

        <div className="p-4">
          {error ? (
            <div
              className="text-[13px] text-center py-10"
              style={{ color: "#C1443A" }}
            >
              {error}
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full rounded-lg bg-black"
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-4 pb-4">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 rounded-lg font-semibold text-[13px]"
            style={{ color: "#6B7480" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCapture}
            disabled={!!error}
            className="px-4 py-2 rounded-lg font-semibold text-[13px] text-white"
            style={{ background: error ? "#E7C6A5" : "#E07A1F" }}
          >
            Capture
          </button>
        </div>
      </div>
    </div>
  );
}
// -------------------------------------------------------------------------

export default function CreateObservation({
  onCancel,
  onSubmit,
  currentUser: propUser,
  defaultProject = "KT-3",
  defaultProjectId = 146,
}) {
  const navigate = useNavigate();

  const currentUser = useMemo(() => {
    if (propUser) return propUser;
    try {
      const data = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
      const roles = (data.roles || []).map((r) => String(r).toUpperCase());
      const role = roles.includes("CHECKER")
        ? "checker"
        : roles.includes("MAKER")
          ? "maker"
          : "";
      return {
        user_id: data.user_id,
        username: data.username,
        role_name: role || (localStorage.getItem("ROLE") || "").toLowerCase(),
      };
    } catch (e) {
      return { role_name: (localStorage.getItem("ROLE") || "").toLowerCase() };
    }
  }, [propUser]);

  const handleBackToDashboard = () => {
    if (typeof onCancel === "function") {
      onCancel();
    }
    navigate("/qc/observations");
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [annotationQueue, setAnnotationQueue] = useState([]);
  const [annotationQueueIndex, setAnnotationQueueIndex] = useState(0);
  const [cameraItemKey, setCameraItemKey] = useState(null);

  const [cameraTargetKey, setCameraTargetKey] = useState(null);
  const [geoCoords, setGeoCoords] = useState(null);

  const openObservationAnnotation = (itemKey, fileIndex, file) => {
    setAnnotationQueue([
      { file, fileIndex, itemKey, imageType: "observation" },
    ]);
    setAnnotationQueueIndex(0);
  };

  const handleAnnotatedObservationSave = (annotatedFile) => {
    const currentItem = annotationQueue[annotationQueueIndex];
    if (!currentItem) return;

    setItems((currentItems) =>
      currentItems.map((item) => {
        if (item.key !== currentItem.itemKey) return item;

        const updatedAttachments = [...(item.attachments || [])];
        if (updatedAttachments[currentItem.fileIndex]) {
          updatedAttachments[currentItem.fileIndex] = {
            ...updatedAttachments[currentItem.fileIndex],
            file: annotatedFile,
          };
        }

        return {
          ...item,
          attachments: updatedAttachments,
        };
      }),
    );

    setAnnotationQueue((prevQueue) =>
      prevQueue.map((entry, idx) =>
        idx === annotationQueueIndex ? { ...entry, file: annotatedFile } : entry,
      ),
    );

    if (annotationQueueIndex < annotationQueue.length - 1) {
      setAnnotationQueueIndex((prev) => prev + 1);
    } else {
      closeAnnotationQueue();
    }
  };

  const handleAnnotationSkip = () => {
    if (annotationQueueIndex < annotationQueue.length - 1) {
      setAnnotationQueueIndex((prev) => prev + 1);
    } else {
      closeAnnotationQueue();
    }
  };

  const handleAnnotationPrevious = () => {
    setAnnotationQueueIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleAnnotationNext = () => {
    if (annotationQueueIndex < annotationQueue.length - 1) {
      setAnnotationQueueIndex((prev) => prev + 1);
    }
  };

  const closeAnnotationQueue = () => {
    setAnnotationQueue([]);
    setAnnotationQueueIndex(0);
  };

  const handleObservationCameraCapture = (capturedFile) => {
    if (!cameraItemKey) return;
    addPhotos(cameraItemKey, [capturedFile]);
    setCameraItemKey(null);
  };

  const accessibleProjects = useMemo(() => {
    try {
      const storedActivePid =
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        localStorage.getItem("PROJECT_ID") ||
        localStorage.getItem("project_id") ||
        "";
      const storedActivePname =
        localStorage.getItem("ACTIVE_PROJECT_NAME") ||
        localStorage.getItem("PROJECT_NAME") ||
        localStorage.getItem("project_name") ||
        "";

      let cachedProjects = [];
      try {
        const rawCached =
          localStorage.getItem("projects") ||
          localStorage.getItem("PROJECTS") ||
          localStorage.getItem("PROJECT_LIST") ||
          "[]";
        cachedProjects = JSON.parse(rawCached) || [];
      } catch {}

      const directAccesses = JSON.parse(
        localStorage.getItem("ACCESSES") || "[]",
      );

      const userData = JSON.parse(
        localStorage.getItem("USER_DATA") || "{}",
      );

      const rawAccesses =
        Array.isArray(directAccesses) && directAccesses.length > 0
          ? directAccesses
          : Array.isArray(userData.accesses)
            ? userData.accesses
            : Array.isArray(userData.ACCESSES)
              ? userData.ACCESSES
              : [];

      const resolveProjectName = (access) => {
        const directName = access.project_name || access.name || access.title || access.project;
        if (directName && typeof directName === "string" && !/^project\s*#?\s*\d+$/i.test(directName.trim())) {
          return directName.trim();
        }
        if (
          storedActivePid &&
          String(access.project_id) === String(storedActivePid) &&
          storedActivePname &&
          !/^project\s*#?\s*\d+$/i.test(storedActivePname.trim())
        ) {
          return storedActivePname.trim();
        }
        if (Array.isArray(cachedProjects)) {
          const match = cachedProjects.find(
            (cp) => String(cp?.id || cp?.project_id) === String(access.project_id),
          );
          const cName = match?.name || match?.project_name || match?.title;
          if (cName && !/^project\s*#?\s*\d+$/i.test(String(cName).trim())) {
            return String(cName).trim();
          }
        }
        return `Project ${access.project_id}`;
      };

      // Keep only one entry for each project_id
      return Array.from(
        new Map(
          rawAccesses
            .filter((access) => access?.project_id)
            .map((access) => [
              String(access.project_id),
              {
                project_id: access.project_id,
                project_name: resolveProjectName(access),
              },
            ]),
        ).values(),
      );
    } catch (error) {
      console.error("Failed to read project accesses:", error);
      return [];
    }
  }, []);

  const firstAccessibleProject = accessibleProjects[0] || null;

  const [meta, setMeta] = useState(() => ({
    observation_name: "",
    project: firstAccessibleProject?.project_id || "",
    project_name: firstAccessibleProject?.project_name || "",
    category: "",
    category_name: "",
    sub_category: "",
    sub_category_name: "",
    tower: "",
    tower_name: "",
    floor: "",
    floor_name: "",
    custom_location: "",
    attendees: [],
  }));

  const [items, setItems] = useState([createEmptyItem()]);
  const [globalAssignees, setGlobalAssignees] = useState([]);
  const [projectMakers, setProjectMakers] = useState([]);
  const [makerSearch, setMakerSearch] = useState("");
  const [assigneeRoleFilter, setAssigneeRoleFilter] = useState("all"); // "all" | "maker" | "checker"
  const [fetchedProjects, setFetchedProjects] = useState(accessibleProjects);
  const [fetchedTowers, setFetchedTowers] = useState([]);
  const [fetchedFloors, setFetchedFloors] = useState([]);
  const [fetchedCategories, setFetchedCategories] = useState([]);

  // Enrich project list with real project names from backend
  useEffect(() => {
    getProjectsForCurrentUser()
      .then((res) => {
        const raw = res?.data || [];
        if (!Array.isArray(raw) || raw.length === 0) return;

        const normalized = raw
          .filter((p) => p && (p.id != null || p.project_id != null))
          .map((p) => {
            const pid = p.id ?? p.project_id ?? p.project;
            const pname =
              p.name ??
              p.project_name ??
              p.title ??
              (p.project ? `Project ${p.project}` : `Project ${pid}`);
            return {
              project_id: pid,
              id: pid,
              project_name: pname,
              name: pname,
            };
          });

        if (normalized.length > 0) {
          const nameMap = new Map(
            normalized.map((p) => [String(p.project_id), p.project_name]),
          );

          setFetchedProjects((prev) => {
            if (prev && prev.length > 0) {
              return prev.map((p) => {
                const realName = nameMap.get(String(p.project_id || p.id));
                const finalName =
                  realName && !/^project\s*#?\s*\d+$/i.test(realName.trim())
                    ? realName
                    : p.project_name || p.name;
                return {
                  ...p,
                  project_name: finalName,
                  name: finalName,
                };
              });
            }
            return normalized;
          });

          // Also update meta.project_name if currently synthetic or empty
          setMeta((prevMeta) => {
            if (!prevMeta.project) {
              const firstP = normalized[0];
              if (firstP) {
                return {
                  ...prevMeta,
                  project: firstP.project_id || firstP.id,
                  project_name: firstP.project_name || firstP.name,
                };
              }
              return prevMeta;
            }

            const match = normalized.find(
              (p) => String(p.project_id || p.id) === String(prevMeta.project),
            );
            if (
              match &&
              match.project_name &&
              !/^project\s*#?\s*\d+$/i.test(match.project_name.trim()) &&
              (!prevMeta.project_name ||
                /^project\s*#?\s*\d+$/i.test(prevMeta.project_name.trim()))
            ) {
              return {
                ...prevMeta,
                project_name: match.project_name,
              };
            }
            return prevMeta;
          });
        }
      })
      .catch((err) => {
        console.error("Failed to enrich QC observation project names:", err);
      });
  }, []);

const filteredMakers = useMemo(() => {
  const search = makerSearch.trim().toLowerCase();
  let list = projectMakers;
  if (assigneeRoleFilter !== "all") {
    list = list.filter((user) => (user.role_name || "maker") === assigneeRoleFilter);
  }
  if (!search) return list;
  return list.filter((user) => {
    const searchableText = [
      user.username,
      user.user_name,
      user.name,
      user.full_name,
      user.display_name,
      user.email,
      user.role_name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchableText.includes(search);
  });
}, [projectMakers, makerSearch, assigneeRoleFilter]);

  // useEffect(() => {
  //   getProjectsForCurrentUser()
  //     .then((res) => {
  //       const data = res?.data || [];
  //       setFetchedProjects(Array.isArray(data) ? data : []);
  //     })
  //     .catch((err) => console.error("Failed to fetch projects:", err));
  // }, []);


  useEffect(() => {
    if (meta.project) {
      getUsersByProject(meta.project)
        .then((res) => {
          const users = res.data || [];
          console.log("Fetched users for project:", users);
          const assignableUsers = [];
          users.forEach((u) => {
            const isMaker =
              (u.roles &&
                u.roles
                  .map((r) => String(r).toUpperCase())
                  .includes("MAKER")) ||
              (u.role && String(u.role).toUpperCase() === "MAKER") ||
              (u.role_name && String(u.role_name).toUpperCase() === "MAKER");
            const isChecker =
              (u.roles &&
                u.roles
                  .map((r) => String(r).toUpperCase())
                  .includes("CHECKER")) ||
              (u.role && String(u.role).toUpperCase() === "CHECKER") ||
              (u.role_name && String(u.role_name).toUpperCase() === "CHECKER");

            const uid = u.user_id ?? u.id;
            const uname = u.user_name ?? u.username ?? u.name;

            if (isMaker) {
              assignableUsers.push({
                ...u,
                user_id: uid,
                user_name: uname,
                role_name: "maker",
              });
            } else if (isChecker) {
              assignableUsers.push({
                ...u,
                user_id: uid,
                user_name: uname,
                role_name: "checker",
              });
            }
          });
          console.log("Filtered project assignees (makers & checkers):", assignableUsers);
          setProjectMakers(assignableUsers);
          setGlobalAssignees([]);
          setMakerSearch("");
          setAssigneeRoleFilter("all");
        })
        .catch((err) => {
          console.error("Failed to fetch project users:", err);
        });

      fetchTowersByProject(meta.project)
        .then((res) =>
          setFetchedTowers(Array.isArray(res?.data) ? res.data : []),
        )
        .catch((err) => console.error("Failed to fetch towers:", err));

      getCategoryTreeByProject(meta.project)
        .then((res) =>
          setFetchedCategories(Array.isArray(res?.data) ? res.data : []),
        )
        .catch((err) => console.error("Failed to fetch categories:", err));
    } else {
      setFetchedTowers([]);
      setFetchedCategories([]);
      setProjectMakers([]);
      setGlobalAssignees([]);
      setMakerSearch("");
      setAssigneeRoleFilter("all");
    }
  }, [meta.project]);

  useEffect(() => {
    if (meta.tower) {
      getLevelsByTowerId(meta.tower)
        .then((res) =>
          setFetchedFloors(Array.isArray(res?.data) ? res.data : []),
        )
        .catch((err) => console.error("Failed to fetch floors:", err));
    } else {
      setFetchedFloors([]);
    }
  }, [meta.tower]);

  if (currentUser?.role_name !== "checker") {
    return (
      <div className="min-h-screen p-8 font-sans flex items-center justify-center bg-[#F6F5F1]">
        <div className="text-center p-8 bg-white rounded-xl border border-[#DEDAD1]">
          <h2 className="text-xl font-bold text-[#C1443A] mb-2">
            Access Denied
          </h2>
          <p className="text-[#6B7480] mb-4">
            Only users with the 'checker' role can raise QC observations.
          </p>
          <button
            onClick={() => navigate("/qc/observations")}
            className="px-4 py-2 bg-[#1B2430] text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            Go to QC Dashboard
          </button>
        </div>
      </div>
    );
  }

  const updateMeta = (key, value) => {
    setMeta((current) => ({ ...current, [key]: value }));
  };

  const updateItem = (key, patch) => {
    setItems((current) =>
      current.map((item) => (item.key === key ? { ...item, ...patch } : item)),
    );
  };

  const removeItem = (key) => {
    setItems((current) =>
      current.length > 1 ? current.filter((item) => item.key !== key) : current,
    );
  };

  const addPhotos = (itemKey, files) => {
    const fileList = Array.from(files || []);
    if (!fileList.length) return;

    let queueItems = [];

    setItems((current) =>
      current.map((item) => {
        if (item.key !== itemKey) return item;

        const previousCount = item.attachments ? item.attachments.length : 0;
        const newAttachments = fileList.map((file) => ({
          file,
          type: "observation",
        }));

        queueItems = fileList.map((file, offset) => ({
          file,
          fileIndex: previousCount + offset,
          itemKey,
          imageType: "observation",
        }));

        return {
          ...item,
          attachments: [...(item.attachments || []), ...newAttachments],
        };
      }),
    );

    if (queueItems.length > 0) {
      setAnnotationQueue(queueItems);
      setAnnotationQueueIndex(0);
    }
  };

  const removePhoto = (itemKey, photoIndex) => {
    setItems((current) =>
      current.map((item) =>
        item.key === itemKey
          ? {
              ...item,
              attachments: item.attachments.filter(
                (_, index) => index !== photoIndex,
              ),
            }
          : item,
      ),
    );
  };

  // NEW: called when the camera modal captures a photo
  const handleCameraCapture = (file) => {
    if (!cameraTargetKey) return;
    addPhotos(cameraTargetKey, [file]);
    setCameraTargetKey(null);
  };

  const selectNamedEntity = (
    idKey,
    nameKey,
    rawValue,
    options,
    idField,
    nameField,
  ) => {
    const selected = options.find(
      (option) => String(option[idField]) === String(rawValue),
    );

    setMeta((current) => ({
      ...current,
      [idKey]: rawValue,
      [nameKey]: selected?.[nameField] || rawValue,
    }));
  };

  const hasSelectedMaker = globalAssignees.some(
    (a) => String(a.role_name || "").toLowerCase() === "maker",
  );

  const canSubmit =
    meta.observation_name.trim() &&
    meta.project &&
    meta.category &&
    (meta.tower || meta.custom_location.trim()) &&
    hasSelectedMaker &&
    items.every((item) => item.gap_type.trim() && item.description.trim());

  const buildFormDataForItem = (item, itemIndex) => {
    const formData = new FormData();

    Object.entries({
      ...meta,
      attendees: JSON.stringify(meta.attendees),
      created_by_id: currentUser.user_id,
      created_by_name: currentUser.username,
      created_by_role_name: currentUser.role_name.toLowerCase(),
    }).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        formData.append(key, value);
      }
    });

    if (item.repetition) {
      formData.append("items[0][repetition]", item.repetition);
    }

    formData.append("items[0][gap_type]", item.gap_type);
    formData.append("items[0][description]", item.description);

    if (item.resolution_advised) {
      formData.append("items[0][resolution_advised]", item.resolution_advised);
    }

    globalAssignees.forEach((assignee, assigneeIndex) => {
      formData.append(
        `items[0][assignments][${assigneeIndex}][user_id]`,
        assignee.user_id,
      );
      formData.append(
        `items[0][assignments][${assigneeIndex}][user_name]`,
        assignee.user_name,
      );
      formData.append(
        `items[0][assignments][${assigneeIndex}][role_name]`,
        assignee.role_name,
      );
    });

    (item.attachments || []).forEach((attachment, attachmentIndex) => {
      formData.append(
        `items[0][attachments][${attachmentIndex}][type]`,
        attachment.type,
      );
      formData.append(
        `items[0][attachments][${attachmentIndex}][file]`,
        attachment.file,
      );
    });

    return formData;
  };

  const handleSubmit = async (location) => {
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const activeCoords = location || geoCoords;
      const responses = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const formData = buildFormDataForItem(item, i);
        if (activeCoords?.latitude && activeCoords?.longitude) {
          formData.append("latitude", activeCoords.latitude);
          formData.append("longitude", activeCoords.longitude);
          if (activeCoords.accuracy) {
            formData.append("accuracy", activeCoords.accuracy);
          }
        }

        const res = await submitQCObservation(formData);
        responses.push(res);
      }

      if (onSubmit) {
        await onSubmit(responses, { meta, items });
      } else {
        navigate("/qc/observations");
      }
    } catch (error) {
      console.error("Failed to submit observation:", error);
      alert("Failed to submit observation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const crumbs = [
    meta.project_name,
    meta.phase_name,
    meta.stage_name,
    meta.tower_name,
    meta.floor_name || meta.custom_location,
  ].filter(Boolean);

  return (
    <div
      className="min-h-screen p-8 font-sans"
      style={{ background: "#F6F5F1" }}
    >
      <style>{`
        .tag-corner {
          clip-path: polygon(
            0 0,
            calc(100% - 18px) 0,
            100% 18px,
            100% 100%,
            0 100%
          );
          border-radius: 4px;
        }

        input:focus,
        textarea:focus,
        select:focus {
          box-shadow: 0 0 0 3px rgba(224, 122, 31, 0.18);
          border-color: #E07A1F;
        }
      `}</style>

      <div className="max-w-[1100px] mx-auto">
        <button
          type="button"
          onClick={handleBackToDashboard}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 -ml-2.5 rounded-lg text-[13.5px] font-semibold mb-3 hover:bg-[#EFEDE7] hover:text-[#1B2430] transition-all cursor-pointer"
          style={{ color: "#6B7480" }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div
          className="mb-2 text-[12px] font-semibold tracking-widest uppercase"
          style={{ color: "#E07A1F" }}
        >
          New Report
        </div>

        <h1 className="text-[32px] font-bold mb-1" style={{ color: "#1B2430" }}>
          Raise Observation
        </h1>

        <p className="text-[14px] mb-6" style={{ color: "#6B7480" }}>
          Log a site observation and assign it to the responsible maker.
        </p>

        {crumbs.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-6 text-[12.5px]">
            {crumbs.map((crumb, index) => (
              <React.Fragment key={`${crumb}-${index}`}>
                <span
                  className="px-2.5 py-1 rounded-full font-medium"
                  style={{ background: "#EFEDE7", color: "#3A4552" }}
                >
                  {crumb}
                </span>
                {index < crumbs.length - 1 && (
                  <span style={{ color: "#B7BEC9" }}>/</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        <div
          className="bg-white rounded-xl border p-6 mb-6"
          style={{ borderColor: "#DEDAD1" }}
        >
          <div className="flex items-center gap-2 mb-5">
            <MapPin size={16} style={{ color: "#E07A1F" }} />
            <div className="text-[17px] font-bold" style={{ color: "#1B2430" }}>
              Observation location
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <FieldLabel required>Project</FieldLabel>
              <Select
                value={meta.project}
                onChange={(value) => {
                  if (
                    fetchedProjects.length > 0 &&
                    typeof fetchedProjects[0] === "object"
                  ) {
                    const p = fetchedProjects.find(
                      (proj) =>
                        String(proj.project_id || proj.id) === String(value),
                    );
                    if (p) {
                      updateMeta("project", p.project_id || p.id);
                      updateMeta(
                        "project_name",
                        p.project_name || p.name || p.title || p.project,
                      );
                    }
                  } else {
                    updateMeta("project", value);
                    updateMeta("project_name", value);
                  }
                  updateMeta("tower", "");
                  updateMeta("tower_name", "");
                  updateMeta("floor", "");
                  updateMeta("floor_name", "");
                  updateMeta("category", "");
                  updateMeta("category_name", "");
                }}
                options={fetchedProjects}
                placeholder="Select Project"
                getValue={
                  fetchedProjects.length > 0 &&
                  typeof fetchedProjects[0] === "object"
                    ? (project) => project.project_id || project.id
                    : undefined
                }
                getLabel={
                  fetchedProjects.length > 0 &&
                  typeof fetchedProjects[0] === "object"
                    ? (project) =>
                        project.project_name ||
                        project.name ||
                        project.title ||
                        project.project
                    : undefined
                }
              />
            </div>

            <div>
              <FieldLabel required>Observation Name</FieldLabel>
              <input
                value={meta.observation_name}
                onChange={(event) =>
                  updateMeta("observation_name", event.target.value)
                }
                className={inputCls}
                style={{ borderColor: "#DEDAD1" }}
              />
            </div>

            <div>
              <FieldLabel required>Category</FieldLabel>
              <Select
                value={meta.category}
                onChange={(value) => {
                  if (
                    fetchedCategories.length > 0 &&
                    typeof fetchedCategories[0] === "object"
                  ) {
                    const c = fetchedCategories.find(
                      (cat) => String(cat.id) === String(value),
                    );
                    if (c) {
                      updateMeta("category", c.id);
                      updateMeta("category_name", c.name || c.category_name);
                    }
                  } else {
                    updateMeta("category", value);
                    updateMeta("category_name", value);
                  }
                }}
                options={fetchedCategories}
                placeholder="Select Category"
                getValue={
                  fetchedCategories.length > 0 &&
                  typeof fetchedCategories[0] === "object"
                    ? (cat) => cat.id
                    : undefined
                }
                getLabel={
                  fetchedCategories.length > 0 &&
                  typeof fetchedCategories[0] === "object"
                    ? (cat) => cat.name || cat.category_name
                    : undefined
                }
              />
            </div>

            <div>
              <FieldLabel>Tower</FieldLabel>
              <Select
                value={meta.tower}
                onChange={(value) => {
                  if (
                    fetchedTowers.length > 0 &&
                    typeof fetchedTowers[0] === "object"
                  ) {
                    const t = fetchedTowers.find(
                      (twr) => String(twr.id) === String(value),
                    );
                    if (t) {
                      updateMeta("tower", t.id);
                      updateMeta(
                        "tower_name",
                        t.name || t.tower_name || t.building_name,
                      );
                    }
                  } else {
                    updateMeta("tower", value);
                    updateMeta("tower_name", value);
                  }
                  updateMeta("floor", "");
                  updateMeta("floor_name", "");
                }}
                options={fetchedTowers}
                placeholder="Select Tower"
                getValue={
                  fetchedTowers.length > 0 &&
                  typeof fetchedTowers[0] === "object"
                    ? (twr) => twr.id
                    : undefined
                }
                getLabel={
                  fetchedTowers.length > 0 &&
                  typeof fetchedTowers[0] === "object"
                    ? (twr) => twr.name || twr.tower_name || twr.building_name
                    : undefined
                }
              />
            </div>

            <div>
              <FieldLabel>Floor</FieldLabel>
              <Select
                value={meta.floor}
                onChange={(value) => {
                  if (
                    fetchedFloors.length > 0 &&
                    typeof fetchedFloors[0] === "object"
                  ) {
                    const f = fetchedFloors.find(
                      (flr) => String(flr.id) === String(value),
                    );
                    if (f) {
                      updateMeta("floor", f.id);
                      updateMeta("floor_name", f.name || f.level_name);
                    }
                  } else {
                    updateMeta("floor", value);
                    updateMeta("floor_name", value);
                  }
                }}
                options={fetchedFloors}
                placeholder="Select Floor"
                getValue={
                  fetchedFloors.length > 0 &&
                  typeof fetchedFloors[0] === "object"
                    ? (flr) => flr.id
                    : undefined
                }
                getLabel={
                  fetchedFloors.length > 0 &&
                  typeof fetchedFloors[0] === "object"
                    ? (flr) => flr.name || flr.level_name
                    : undefined
                }
              />
            </div>

            {/* <div>
                            <FieldLabel>Custom Location</FieldLabel>
                            <input
                                value={meta.custom_location}
                                onChange={(event) =>
                                    updateMeta("custom_location", event.target.value)
                                }
                                placeholder="Example: Basement ramp near gate 2"
                                className={inputCls}
                                style={{ borderColor: "#DEDAD1" }}
                            />
                        </div> */}
          </div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardList size={16} style={{ color: "#E07A1F" }} />
            <div className="text-[17px] font-bold" style={{ color: "#1B2430" }}>
              Observation Items
            </div>
          </div>

          <button
            type="button"
            onClick={() =>
              setItems((current) => [...current, createEmptyItem()])
            }
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg font-semibold text-[13px] text-white"
            style={{ background: "#1B2430" }}
          >
            <Plus size={15} />
            Add Item
          </button>
        </div>

        <div className="space-y-5 mb-8">
          {items.map((item, itemIndex) => (
            <div
              key={item.key}
              className="tag-corner bg-white border p-6"
              style={{ borderColor: "#DEDAD1" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div
                  className="text-[15px] font-bold"
                  style={{ color: "#1B2430" }}
                >
                  Observation #{itemIndex + 1}
                </div>

                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(item.key)}
                    className="text-[12.5px] font-semibold flex items-center gap-1"
                    style={{ color: "#C1443A" }}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 mb-5">
                <div>
                  <FieldLabel>Repetition</FieldLabel>
                  <input
                    value={item.repetition}
                    onChange={(event) =>
                      updateItem(item.key, {
                        repetition: event.target.value,
                      })
                    }
                    placeholder="Example: First occurrence"
                    className={inputCls}
                    style={{ borderColor: "#DEDAD1" }}
                  />
                </div>

                <div>
                  <FieldLabel required>Gap Type</FieldLabel>
                  <input
                    value={item.gap_type}
                    onChange={(event) =>
                      updateItem(item.key, {
                        gap_type: event.target.value,
                      })
                    }
                    placeholder="Example: Unsafe Condition"
                    className={inputCls}
                    style={{ borderColor: "#DEDAD1" }}
                  />
                </div>
              </div>

              <div className="mb-5">
                <FieldLabel required>Description</FieldLabel>
                <textarea
                  value={item.description}
                  onChange={(event) =>
                    updateItem(item.key, {
                      description: event.target.value,
                    })
                  }
                  placeholder="Describe the observation in detail."
                  rows={3}
                  className={`${inputCls} resize-none`}
                  style={{ borderColor: "#DEDAD1" }}
                />
              </div>

              <div className="mb-5">
                <FieldLabel>Resolution Advised</FieldLabel>
                <textarea
                  value={item.resolution_advised}
                  onChange={(event) =>
                    updateItem(item.key, {
                      resolution_advised: event.target.value,
                    })
                  }
                  placeholder="Describe the recommended corrective action."
                  rows={3}
                  className={`${inputCls} resize-none`}
                  style={{ borderColor: "#DEDAD1" }}
                />
              </div>

              <div>
                <FieldLabel>Observation Photos</FieldLabel>

                <div className="flex gap-3 flex-wrap">
                  {item.attachments.map((attachment, photoIndex) => (
                    <div
                      key={`${attachment.file.name}-${photoIndex}`}
                      className="flex flex-col items-center"
                    >
                      <div className="relative group">
                        <PhotoThumb file={attachment.file} />
                        <button
                          type="button"
                          onClick={() => removePhoto(item.key, photoIndex)}
                          className="absolute -top-2 -right-2 bg-white rounded-full p-0.5 shadow-md opacity-0 group-hover:opacity-100 transition-opacity border border-gray-200 z-10"
                        >
                          <X size={14} color="#C1443A" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          openObservationAnnotation(
                            item.key,
                            photoIndex,
                            attachment.file,
                          )
                        }
                        className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-semibold w-full justify-center"
                        style={{
                          borderColor: "#F0C28F",
                          color: "#B86519",
                          background: "#FFF8F0",
                        }}
                      >
                        <PenLine size={12} />
                        Annotate
                      </button>
                    </div>
                  ))}

                  <div className="flex flex-wrap items-center gap-2">
                    <label
                      htmlFor={`gallery-upload-${item.key}`}
                      className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3.5 py-2 text-xs font-semibold text-orange-700 transition-colors hover:bg-orange-100"
                    >
                      <UploadCloud className="h-4 w-4" />
                      Select from Gallery
                    </label>
                    <input
                      id={`gallery-upload-${item.key}`}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(event) => {
                        addPhotos(item.key, event.target.files);
                        event.target.value = "";
                      }}
                    />

                    <button
                      type="button"
                      onClick={() => setCameraItemKey(item.key)}
                      className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-orange-600 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-orange-700"
                    >
                      <Camera className="h-4 w-4" />
                      Take Photo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="bg-white rounded-xl border p-6 mb-6"
          style={{ borderColor: "#DEDAD1" }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} style={{ color: "#E07A1F" }} />
            <div className="text-[17px] font-bold" style={{ color: "#1B2430" }}>
              Assign Observation(s)
            </div>
          </div>
          <p className="text-[13px] mb-3" style={{ color: "#6B7480" }}>
            Select one or more Makers to resolve these observations, and optional Checkers for readonly visibility.
          </p>

          {/* Search input */}
          <div className="mb-3">
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: "#8A8F79" }}
              />
              <input
                type="text"
                value={makerSearch}
                onChange={(event) => setMakerSearch(event.target.value)}
                placeholder="Search user by username or role..."
                aria-label="Search users"
                className="w-full rounded-lg border bg-white py-2.5 pl-9 pr-9 text-[13px] outline-none transition focus:ring-2"
                style={{
                  borderColor: "#DEDAD1",
                  color: "#3A4552",
                }}
              />
              {makerSearch && (
                <button
                  type="button"
                  onClick={() => setMakerSearch("")}
                  aria-label="Clear user search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Role filter tabs inside the same card */}
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => setAssigneeRoleFilter("all")}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition border ${
                assigneeRoleFilter === "all"
                  ? "bg-[#1B2430] text-white border-[#1B2430]"
                  : "bg-white text-[#6B7480] border-[#DEDAD1] hover:bg-gray-50"
              }`}
            >
              All ({projectMakers.length})
            </button>
            <button
              type="button"
              onClick={() => setAssigneeRoleFilter("maker")}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition border ${
                assigneeRoleFilter === "maker"
                  ? "bg-[#E07A1F] text-white border-[#E07A1F]"
                  : "bg-white text-[#6B7480] border-[#DEDAD1] hover:bg-gray-50"
              }`}
            >
              Makers ({projectMakers.filter((u) => u.role_name === "maker").length})
            </button>
            <button
              type="button"
              onClick={() => setAssigneeRoleFilter("checker")}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition border ${
                assigneeRoleFilter === "checker"
                  ? "bg-[#4B5563] text-white border-[#4B5563]"
                  : "bg-white text-[#6B7480] border-[#DEDAD1] hover:bg-gray-50"
              }`}
            >
              Checkers (Readonly) ({projectMakers.filter((u) => u.role_name === "checker").length})
            </button>
          </div>

          {/* User chips — filtered list */}
          {projectMakers.length === 0 ? (
            <span className="text-[13px] italic text-[#9A968C]">
              No assignable users found for this project.
            </span>
          ) : filteredMakers.length === 0 && (makerSearch.trim() || assigneeRoleFilter !== "all") ? (
            <div
              className="rounded-lg border px-3 py-3 text-[13px]"
              style={{
                borderColor: "#DEDAD1",
                color: "#8A8F79",
                background: "#FAF9F6",
              }}
            >
              No users found matching &ldquo;{makerSearch}&rdquo;.
            </div>
          ) : (
            <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
              {filteredMakers.map((user) => {
                const userId = user.user_id ?? user.id;
                const userName = user.user_name ?? user.username ?? user.name;
                const isUserMaker = user.role_name === "maker";
                const normalizedUser = {
                  ...user,
                  user_id: userId,
                  user_name: userName,
                  role_name: user.role_name || "maker",
                };

                const active = globalAssignees.some(
                  (assignee) => Number(assignee.user_id) === Number(userId),
                );

                return (
                  <button
                    type="button"
                    key={`${userId}-${normalizedUser.role_name}`}
                    onClick={() => {
                      setGlobalAssignees((prev) => {
                        if (active) {
                          return prev.filter(
                            (a) => Number(a.user_id) !== Number(userId),
                          );
                        } else {
                          return [...prev, normalizedUser];
                        }
                      });
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors cursor-pointer"
                    style={
                      active
                        ? {
                            background: "#1B2430",
                            color: "white",
                            borderColor: "#1B2430",
                          }
                        : {
                            background: "white",
                            color: "#3A4552",
                            borderColor: "#DEDAD1",
                          }
                    }
                  >
                    <Users size={12} />
                    <span>{userName}</span>
                    <span
                      className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                      style={
                        active
                          ? {
                              background: "#3A4552",
                              color: isUserMaker ? "#F6E6CE" : "#EAE8E3",
                            }
                          : isUserMaker
                            ? { background: "#F6E6CE", color: "#B4761F" }
                            : { background: "#EAE8E3", color: "#555E68" }
                      }
                    >
                      {isUserMaker ? "Maker" : "Checker (Readonly)"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Selected Assignees summary */}
          {globalAssignees.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#EFEDE7]">
              <div
                className="text-[11px] font-semibold uppercase mb-2"
                style={{ color: "#8A8F79" }}
              >
                Selected Assignees ({globalAssignees.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {globalAssignees.map((assignee) => {
                  const aid = assignee.user_id ?? assignee.id;
                  const aname = assignee.user_name ?? assignee.username ?? assignee.name;
                  const isAssigneeMaker = assignee.role_name === "maker";
                  return (
                    <button
                      type="button"
                      key={aid}
                      onClick={() =>
                        setGlobalAssignees((prev) =>
                          prev.filter((a) => Number(a.user_id) !== Number(aid)),
                        )
                      }
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-medium border cursor-pointer hover:opacity-90"
                      style={{
                        background: "#1B2430",
                        color: "white",
                        borderColor: "#1B2430",
                      }}
                    >
                      <Users size={12} />
                      <span>{aname}</span>
                      <span
                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                        style={{
                          background: "#3A4552",
                          color: isAssigneeMaker ? "#F6E6CE" : "#EAE8E3",
                        }}
                      >
                        {isAssigneeMaker ? "Maker" : "Checker (Readonly)"}
                      </span>
                      <X size={11} />
                    </button>
                  );
                })}
              </div>

              {!hasSelectedMaker && (
                <div className="text-[12px] text-amber-700 mt-2 font-medium">
                  Please select at least one Maker to resolve these observations.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mb-4">
          <GeoFenceGuard
            siteId={meta.project}
            actionName="Submit Observation"
          >
            {({ location, isValidated }) => (
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  disabled={!canSubmit || isSubmitting || !isValidated}
                  onClick={() => {
                    if (location) setGeoCoords(location);
                    handleSubmit(location);
                  }}
                  className="px-5 py-3 rounded-lg font-semibold text-[14.5px] text-white shadow-sm transition-opacity"
                  style={{
                    background:
                      canSubmit && !isSubmitting && isValidated
                        ? "#E07A1F"
                        : "#E7C6A5",
                    cursor:
                      canSubmit && !isSubmitting && isValidated
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Observation"}
                </button>

                <button
                  type="button"
                  onClick={handleBackToDashboard}
                  className="px-5 py-3 rounded-lg font-semibold text-[14.5px] hover:text-[#1B2430] hover:bg-[#EFEDE7]/60 transition-colors"
                  style={{ color: "#6B7480" }}
                >
                  Cancel
                </button>

                {!canSubmit && (
                  <span className="text-[12.5px]" style={{ color: "#9A968C" }}>
                    Complete the required location, item, and assignee fields.
                  </span>
                )}
              </div>
            )}
          </GeoFenceGuard>
        </div>
      </div>

      <SafetyImageAnnotationModal
        open={annotationQueue.length > 0}
        file={annotationQueue[annotationQueueIndex]?.file || null}
        onClose={closeAnnotationQueue}
        onSave={handleAnnotatedObservationSave}
        currentIndex={annotationQueueIndex}
        totalCount={annotationQueue.length}
        onPrevious={annotationQueueIndex > 0 ? handleAnnotationPrevious : undefined}
        onNext={annotationQueueIndex < annotationQueue.length - 1 ? handleAnnotationNext : undefined}
        onSkip={handleAnnotationSkip}
      />

      <SafetyCameraCaptureModal
        open={Boolean(cameraItemKey)}
        onClose={() => setCameraItemKey(null)}
        onCapture={handleObservationCameraCapture}
        title="Take Observation Photo"
      />

      {/* NEW: camera capture modal */}
      <CameraCaptureModal
        open={Boolean(cameraTargetKey)}
        onClose={() => setCameraTargetKey(null)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
}